import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { Form, Formik, type FormikHelpers } from 'formik';
import { useMemo } from 'react';

import FormField from 'components/common/formikForm/formField';
import FormGridLayout from 'components/common/formikForm/formGridLayout';
import SubmitButton from 'components/common/formikForm/SubmitButton';

import { useCheckEmailAvailability } from 'hooks/useCheckEmail';
import type { AdminUserProfileFormInterface } from 'types';
import {
    adminUserProfileFormDefaultValues,
    buildAdminUserProfileValidationSchema,
} from 'utils/validations/adminPanel';

interface EditUserProfileFormProps {
    initialValues?: AdminUserProfileFormInterface;
    originalEmail?: string;
    loading?: boolean;
    onClose: () => void;
    onSubmit: (
        values: AdminUserProfileFormInterface,
        helpers: FormikHelpers<AdminUserProfileFormInterface>
    ) => void | Promise<void>;
}

export function EditUserProfileForm({
    initialValues,
    originalEmail,
    loading = false,
    onClose,
    onSubmit,
}: EditUserProfileFormProps) {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const checkEmailAvailable = useCheckEmailAvailability();

    const validationSchema = useMemo(
        () => buildAdminUserProfileValidationSchema(checkEmailAvailable, originalEmail),
        [checkEmailAvailable, originalEmail]
    );

    return (
        <Dialog
            open
            fullWidth
            maxWidth="sm"
            fullScreen={fullScreen}
            scroll="paper"
            slotProps={{
                paper: {
                    sx: {
                        display: 'flex',
                        flexDirection: 'column',
                        maxHeight: fullScreen ? '100%' : '90vh',
                        borderRadius: fullScreen ? 0 : 2,
                        m: fullScreen ? 0 : 2,
                    },
                },
            }}
        >
            <Formik
                enableReinitialize
                initialValues={initialValues || adminUserProfileFormDefaultValues}
                validationSchema={validationSchema}
                validateOnBlur
                validateOnChange={false}
                onSubmit={onSubmit}
            >
                <Form
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        flex: 1,
                        minHeight: 0,
                        overflow: 'hidden',
                    }}
                >
                    <DialogTitle sx={{ py: 2 }}>Edit user profile</DialogTitle>

                    <DialogContent dividers sx={{ flex: '1 1 auto', overflowY: 'auto' }}>
                        <FormGridLayout>
                            <FormField name="firstname" label="First name" disabled={loading} />
                            <FormField name="lastname" label="Last name" disabled={loading} />
                            <FormField name="email" label="Email" type="email" disabled={loading} />
                        </FormGridLayout>
                    </DialogContent>

                    <DialogActions
                        sx={{
                            flexShrink: 0,
                            px: { xs: 2, sm: 3 },
                            py: 1.5,
                            gap: 1,
                            flexDirection: { xs: 'column-reverse', sm: 'row' },
                            '& > :not(style)': {
                                width: { xs: '100%', sm: 'auto' },
                                m: '0 !important',
                            },
                        }}
                    >
                        <Button
                            variant="outlined"
                            onClick={onClose}
                            disabled={loading}
                            sx={{ textTransform: 'none' }}
                        >
                            Cancel
                        </Button>
                        <SubmitButton loading={loading} sx={{ textTransform: 'none' }}>
                            Save changes
                        </SubmitButton>
                    </DialogActions>
                </Form>
            </Formik>
        </Dialog>
    );
}

export default EditUserProfileForm;
